import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';

export interface Admin {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
}

export interface AdminFormData {
  name: string;
  phone_number: string;
  role_id?: number;
}

export interface AdminsResponse {
  data: Admin[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// Get all admins
export const useAdmins = (params?: { search?: string; per_page?: number }) => {
  return useQuery({
    queryKey: ['admins', params],
    queryFn: async (): Promise<AdminsResponse> => {
      const response = await api.get('/admins', { params });
      return response.data;
    },
  });
};

// Create admin
export const useCreateAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: AdminFormData): Promise<Admin> => {
      const response = await api.post('/admins', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
    },
  });
};

// Update admin
export const useUpdateAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: AdminFormData }): Promise<Admin> => {
      const response = await api.put(`/admins/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
    },
  });
};

// Delete admin
export const useDeleteAdmin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number): Promise<{ message: string }> => {
      const response = await api.delete(`/admins/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
    },
  });
};

// Get admin statistics
export const useAdminStats = () => {
  return useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      // For now, we'll calculate stats from the admins list
      // In the future, you might want a dedicated stats endpoint
      const response = await api.get('/admins');
      const admins = response.data.data;
      
      return {
        total: admins.length,
        active: admins.filter((admin: Admin) => admin.is_admin).length,
        super_admins: 0, // Not implemented in current API
        this_month: admins.filter((admin: Admin) => {
          const createdDate = new Date(admin.created_at);
          const now = new Date();
          return createdDate.getMonth() === now.getMonth() && 
                 createdDate.getFullYear() === now.getFullYear();
        }).length,
      };
    },
  });
}; 